if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

// ==========================================================================
// Bespoke Editorial Card Quick-Add & Quantity Pill Handler
// ==========================================================================
if (typeof window !== 'undefined') {
  // 1. Initial Add to Cart (0 -> 1)
  window.handleCardQuickAdd = function(e, btn) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }

    if (!btn) return;
    if (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true' || btn.classList.contains('is-loading')) {
      return;
    }

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.classList.add('is-loading');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
    .then(function(response) {
      return response.json().then(function(item) {
        if (!response.ok || item.status) {
          throw new Error(item.description || item.message || 'Error adding to cart');
        }
        return item;
      });
    })
    .then(function(item) {
      btn.classList.remove('is-loading');

      // Immediately update this variant UI to quantity 1
      window.syncCardQuantityUI(variantId, 1);
      // And resync all from cart.js
      window.syncAllCardPillsFromCart();

      // Background update cart drawer & header bubble (DO NOT OPEN DRAWER)
      window.refreshCartState(item);
    })
    .catch(function(err) {
      console.error('Quick add error:', err);
      btn.classList.remove('is-loading');
      btn.classList.add('is-error');
      setTimeout(function() {
        btn.classList.remove('is-error');
      }, 2000);
    });
  };

  // 2. Quantity Adjustment (+ or -)
  window.handleCardQuantityAdjust = function(e, btn, delta) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }

    if (!btn) return;
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    var wrapper = btn.closest('.card-quick-add-wrapper');
    var pill = wrapper ? wrapper.querySelector('.card-quantity-pill') : null;
    var countEl = pill ? pill.querySelector('.card-quantity-pill__count') : null;
    var currentQty = countEl ? (parseInt(countEl.textContent, 10) || 0) : 0;
    var newQty = currentQty + delta;
    if (newQty < 0) newQty = 0;

    // Optimistic UI update across all cards matching this variant
    window.syncCardQuantityUI(variantId, newQty);

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: newQty
      })
    })
    .then(function(response) {
      return response.json().then(function(cart) {
        if (!response.ok || cart.status) {
          throw new Error(cart.description || cart.message || 'Error updating cart');
        }
        return cart;
      });
    })
    .then(function(cart) {
      var actualQty = 0;
      if (cart.items && cart.items.length) {
        cart.items.forEach(function(item) {
          if (String(item.variant_id || item.id) === String(variantId)) {
            actualQty += item.quantity;
          }
        });
      }
      window.syncCardQuantityUI(variantId, actualQty);
      // Refresh background cart drawer & header bubble
      window.refreshCartState(cart);
    })
    .catch(function(err) {
      console.error('Quantity adjust error:', err);
      // Revert to currentQty on error
      window.syncCardQuantityUI(variantId, currentQty);
    });
  };

  // 3. UI Synchronization across all cards on page for a specific variant (Strict Mutually Exclusive)
  window.syncCardQuantityUI = function(variantId, qty) {
    var wrappers = document.querySelectorAll('.card-quick-add-wrapper[data-variant-id="' + variantId + '"]');
    wrappers.forEach(function(wrap) {
      if (wrap.closest('.custom-package-builder')) return;
      var addBtn = wrap.querySelector('.card-quick-add-btn');
      var pill = wrap.querySelector('.card-quantity-pill');
      var countEl = pill ? pill.querySelector('.card-quantity-pill__count') : null;

      if (qty > 0) {
        wrap.classList.add('is-in-cart');
        if (addBtn) {
          addBtn.style.setProperty('display', 'none', 'important');
          addBtn.style.visibility = 'hidden';
          addBtn.style.opacity = '0';
        }
        if (pill) {
          pill.style.setProperty('display', 'inline-flex', 'important');
          pill.style.visibility = 'visible';
          pill.style.opacity = '1';
        }
        if (countEl) countEl.textContent = qty;
      } else {
        wrap.classList.remove('is-in-cart');
        if (pill) {
          pill.style.setProperty('display', 'none', 'important');
          pill.style.visibility = 'hidden';
          pill.style.opacity = '0';
        }
        if (addBtn) {
          addBtn.style.display = '';
          addBtn.style.visibility = '';
          addBtn.style.opacity = '';
        }
        if (countEl) countEl.textContent = '1';
      }
    });
  };

  // 4. Initial Sync of all cards from /cart.js on page load
  window.syncAllCardPillsFromCart = function() {
    fetch('/cart.js')
      .then(function(res) { return res.json(); })
      .then(function(cart) {
        var itemMap = {};
        if (cart.items && cart.items.length) {
          cart.items.forEach(function(item) {
            var id = String(item.variant_id || item.id);
            itemMap[id] = (itemMap[id] || 0) + item.quantity;
          });
        }

        var wrappers = document.querySelectorAll('.card-quick-add-wrapper[data-variant-id]');
        wrappers.forEach(function(wrap) {
          var vId = String(wrap.getAttribute('data-variant-id'));
          var qty = itemMap[vId] || 0;
          var addBtn = wrap.querySelector('.card-quick-add-btn');
          var pill = wrap.querySelector('.card-quantity-pill');
          var countEl = pill ? pill.querySelector('.card-quantity-pill__count') : null;

          if (qty > 0) {
            wrap.classList.add('is-in-cart');
            if (addBtn) {
              addBtn.style.setProperty('display', 'none', 'important');
              addBtn.style.visibility = 'hidden';
              addBtn.style.opacity = '0';
            }
            if (pill) {
              pill.style.setProperty('display', 'inline-flex', 'important');
              pill.style.visibility = 'visible';
              pill.style.opacity = '1';
            }
            if (countEl) countEl.textContent = qty;
          } else {
            wrap.classList.remove('is-in-cart');
            if (pill) {
              pill.style.setProperty('display', 'none', 'important');
              pill.style.visibility = 'hidden';
              pill.style.opacity = '0';
            }
            if (addBtn) {
              addBtn.style.display = '';
              addBtn.style.visibility = '';
              addBtn.style.opacity = '';
            }
            if (countEl) countEl.textContent = '1';
          }
        });
      })
      .catch(function(e) {
        console.error('Error syncing card pills:', e);
      });
  };

  // 5. Background Cart State Updater (Header bubble & Drawer HTML WITHOUT opening)
  window.refreshCartState = function(cartOrItem) {
    // Update header bubble count
    fetch('/?section_id=cart-icon-bubble')
      .then(function(res) { return res.text(); })
      .then(function(bubbleHtml) {
        var bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        var newBubble = bubbleDoc.querySelector('#cart-icon-bubble') || bubbleDoc.querySelector('.shopify-section');
        var curBubble = document.querySelector('#cart-icon-bubble');
        if (newBubble && curBubble) {
          curBubble.innerHTML = newBubble.innerHTML;
        }
      })
      .catch(function() {});

    // Update cart drawer HTML in background (DO NOT OPEN)
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      fetch('/?section_id=cart-drawer')
        .then(function(res) { return res.text(); })
        .then(function(html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var newDrawer = doc.querySelector('cart-drawer');
          if (newDrawer) {
            cartDrawer.innerHTML = newDrawer.innerHTML;
            cartDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
          }
          if (typeof cartDrawer.setupCheckoutHandler === 'function') {
            cartDrawer.setupCheckoutHandler();
          }
          if (typeof cartDrawer.updateProgressBar === 'function') {
            cartDrawer.updateProgressBar();
          }
          var overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
          if (overlay) {
            overlay.addEventListener('click', function() { cartDrawer.close(); });
          }
        })
        .catch(function(err) {
          console.error('Error refreshing background drawer:', err);
        });
    }

    // Publish cartUpdate event so other theme listeners stay in sync
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'card-quick-add',
        cartData: cartOrItem
      });
    }
  };

  // Bind click listener for quick add
  if (!window._editorialQuickAddBound) {
    window._editorialQuickAddBound = true;
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.card-quick-add-btn');
      if (!btn || btn.closest('.custom-package-builder')) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      window.handleCardQuickAdd(e, btn);
    }, true);
  }

  // Bind initial sync on page load and pubsub
  if (typeof window._editorialPillsInit === 'undefined') {
    window._editorialPillsInit = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', window.syncAllCardPillsFromCart);
    } else {
      window.syncAllCardPillsFromCart();
    }

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function() {
        window.syncAllCardPillsFromCart();
      });
    }
  }
}

